import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
// MatSelectModule and MatListModule removed as UI no longer uses them
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { PosTerminalService, PosTerminal } from '../../services/pos/pos-terminal.service';
import { TablesService } from '../../services/tables/tables.service';
import { TableInfo } from '../../services/tables/interfaces/table.interface';
import { MenuProductService } from '../../services/menu-product/menu-product.service';
import { MenuProduct } from '../../models/menu-product.model';
import { OrderService } from '../../services/orders/order.service';
import { OrderDetailService } from '../../services/orders/order-detail.service';

@Component({
  selector: 'app-pos',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    
    LoadingSpinnerComponent,
  ],
  template: `
  <div class="container-fluid">
    <div class="row mb-3 align-items-center">
      <div class="col-auto"><h4 class="mb-0">POS</h4></div>
      <div class="col-auto" *ngIf="isLoading"><app-loading-spinner></app-loading-spinner></div>
    </div>
    
    <!-- Vista simplificada: solo escanear o ingresar código -->
    <div class="row g-3 mb-3">
      <div class="col-12">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div>
            <strong>Estación:</strong> {{ getStationName() || 'No seleccionada' }}
          </div>
          <div *ngIf="!selectedStationUuid" class="alert alert-warning py-1 px-2 mb-0">Seleccione un POS desde el menú.</div>
        </div>

        <label class="form-label">Código de barras / SKU</label>
        <div class="input-group">
          <input class="form-control" type="text" [(ngModel)]="barcode" (keyup.enter)="onAddByCode()" placeholder="Escanee o escriba el código" />
          <button class="btn btn-primary" (click)="onAddByCode()" [disabled]="isLoading || !barcode">Agregar</button>
          <button class="btn btn-outline-secondary" (click)="onScanStub()" [disabled]="isLoading">
            <mat-icon fontSet="material-icons-outlined">qr_code_scanner</mat-icon>
          </button>
        </div>
        <small class="text-muted">Integración del escáner se inyectará aquí más adelante.</small>
      </div>
    </div>
  </div>
  `,
  styles: [``]
})
export class PosPageComponent implements OnInit {
  isLoading = false;
  stations: TableInfo[] = [];
  selectedStationUuid?: string;
  customerName: string = '';
  barcode: string = '';
  orderUuid: string = '';
  products: MenuProduct[] = [];
  orderItems: Array<{ sku?: string; name: string; price: number; qty: number; menuProductUuid: string }>= [];

  constructor(
    private posTerminalService: PosTerminalService,
    private tablesService: TablesService,
    private menuProductService: MenuProductService,
    private orderService: OrderService,
    private orderDetailService: OrderDetailService,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    try {
      const paramStation = this.route.snapshot.paramMap.get('stationUuid') || undefined;
      await this.reloadStations();
      if (paramStation) {
        // Seleccionar la estación indicada por la ruta si existe
        const exists = this.stations.find(s => s.uuid === paramStation);
        this.selectedStationUuid = exists ? exists.uuid : undefined;
      }
      this.products = (await this.menuProductService.getMenuProducts().toPromise()) || [];
    } catch (e) { console.error('[POS] init error', e); } finally { this.isLoading = false; }
  }

  async reloadStations() {
    try {
      const all = await this.posTerminalService.getTerminals().toPromise();
      // Mapear a TableInfo para reutilizar UI existente
      this.stations = (all || []).map(t => ({
        uuid: t.uuid,
        name: t.name,
        status: (t.status || 'IDLE'),
        enabled: t.enabled,
        updatedAt: t.updatedAt,
        total: t.total,
        type: 'PICKUP'
      } as TableInfo));
    } catch (e) { console.error('[POS] reloadStations', e); }
  }

  async createDefaultStation() {
    this.isLoading = true;
    try {
      const payload: any = { name: 'POS1', status: 'IDLE', enabled: true, type: 'PICKUP' };
      const created = await this.tablesService.createTable(payload as any).toPromise();
      await this.reloadStations();
      this.selectedStationUuid = created?.uuid || this.stations.find(s => s.name === 'POS1')?.uuid;
    } catch (e) { console.error('[POS] createDefaultStation', e); } finally { this.isLoading = false; }
  }

  async ensureOrder() {
    if (!this.selectedStationUuid) return;
    this.isLoading = true;
    try {
      const order = await this.orderService.createOrder({ tableId: this.selectedStationUuid, clientName: this.customerName } as any).toPromise();
      const idCandidate = (order as any)?.uuid || (order as any)?.hash || (order as any)?.id || (order as any)?.orderUuid || (order as any)?.hashId;
      this.orderUuid = String(idCandidate || '');
    } catch (e) { console.error('[POS] ensureOrder', e); } finally { this.isLoading = false; }
  }

  async onAddByCode() {
    const code = (this.barcode || '').trim();
    if (!code) return;
    if (!this.orderUuid) { await this.ensureOrder(); }
    if (!this.orderUuid) return; // si aún falla crear la orden

    const found = (this.products || []).find(p => String((p as any).sku || '').trim() === code);
    if (!found) { console.warn('Producto no encontrado por código:', code); return; }
    this.isLoading = true;
    try {
      const created = await this.orderDetailService.createOrderDetail({ orderUuid: this.orderUuid, menuProductUuid: (found as any).uuid, qty: 1 }).toPromise();
      this.orderItems.push({ sku: (found as any).sku, name: found.name, price: Number((found as any).sellPrice || 0), qty: created?.qty ?? 1, menuProductUuid: (found as any).uuid });
      this.barcode = '';
    } catch (e) { console.error('[POS] addByCode', e); } finally { this.isLoading = false; }
  }

  onScanStub() {
    // Punto de integración: aquí inyectaremos el microfrontend o componente externo
    console.log('[POS] Scan stub pressed.');
  }

  resetOrder() {
    this.orderUuid = '';
    this.orderItems = [];
  }

  getTotal(): number { return this.orderItems.reduce((s, it) => s + (Number(it.price) * Number(it.qty)), 0); }

  getStationName(): string {
    return this.stations.find(s => s.uuid === this.selectedStationUuid)?.name || '';
  }
}